// All dates and numbers pass through here so the locale is one switch.
const LOCALE = "en";

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat(LOCALE, { month: "short", day: "numeric", year: "numeric" }).format(d);
  const time = new Intl.DateTimeFormat(LOCALE, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(d); // h23, not hour12:false (2026-11-01): newer ICU wrote "24:05" for the hour after midnight
  return `${date} · ${time}`;
}

/** Whether the moment falls in the viewer's current year (both read in the viewer's own time zone). */
function inViewersYear(d: Date, now: number): boolean { return d.getFullYear() === new Date(now).getFullYear(); }

/** A date alone, without the year when it is the viewer's current one: "Oct 3", "Oct 3, 2025". */
export function formatDay(iso: string, now: number = Date.now()): string {
  const d = new Date(iso);
  const opts: Intl.DateTimeFormatOptions = inViewersYear(d, now) ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" };
  return new Intl.DateTimeFormat(LOCALE, opts).format(d);
}

/** An entry's date and time, without the year when it is the viewer's current one: "Oct 24 · 10:13", "Dec 31, 2025 · 23:50".
 *  `formatDateTime` keeps the full date for every other screen. */
export function formatWhen(iso: string, now: number = Date.now()): string {
  const time = new Intl.DateTimeFormat(LOCALE, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso));
  return `${formatDay(iso, now)} · ${time}`;
}

/** Calendar days from the viewer's today to the moment (both in the viewer's own time zone): 0 today, 1 tomorrow, -1 yesterday. */
function calendarDaysAhead(d: Date, now: number): number {
  const n = new Date(now);
  const start = (x: Date) => Date.UTC(x.getFullYear(), x.getMonth(), x.getDate());
  return Math.round((start(d) - start(n)) / 86_400_000);
}

/** The status menu round (2026-11-02): a short "when" for a snoozed Status field, never with a "·" of its own. "14:00" the same day,
 *  "Tue 09:00" one to six days ahead, "Nov 9, 14:00" further out (so a "Next week" snooze never reads as today's weekday). */
export function formatSoon(iso: string, now: number = Date.now()): string {
  const d = new Date(iso);
  const time = new Intl.DateTimeFormat(LOCALE, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(d);
  const days = calendarDaysAhead(d, now);
  if (days === 0) return time;
  if (days >= 1 && days <= 6) return `${new Intl.DateTimeFormat(LOCALE, { weekday: "short" }).format(d)} ${time}`;
  return `${new Intl.DateTimeFormat(LOCALE, { month: "short", day: "numeric" }).format(d)}, ${time}`;
}

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(LOCALE, { day: "numeric", month: "short", year: "numeric" }).format(new Date(iso));
}

export function formatRelative(iso: string, now: number = Date.now()): string {
  const diff = Math.max(0, now - new Date(iso).getTime());
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} d ago`;
  return formatDay(iso, now);
}

/** Short age for list rows: "5 min", "19 h", "2 d", then the date. */
export function formatAge(iso: string, now: number = Date.now()): string {
  const m = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${Math.max(1, m)} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h`;
  const d = Math.round(h / 24);
  if (d < 14) return `${d} d`;
  return formatDay(iso, now);
}

export function formatNumber(n: number): string { return new Intl.NumberFormat(LOCALE).format(n); }
export function formatPercent(fraction: number): string { return `${Math.round(fraction * 100)} %`; }
export function formatMoney(amount: number, currency: string, /** cents too, for unit prices */ exact = false): string {
  return new Intl.NumberFormat(LOCALE, { style: "currency", currency, minimumFractionDigits: exact ? 2 : 0, maximumFractionDigits: exact ? 2 : 0 }).format(amount);
}
export function initialsOf(name: string): string {
  return name.split(/\s+/).map((p) => p[0] ?? "").join("").slice(0, 2).toUpperCase();
}
export function cx(...parts: Array<string | false | null | undefined>): string { return parts.filter(Boolean).join(" "); }

/** The customer sidebar round (2026-11-01): a language or a country by name in the app's locale. A two-letter code is shown as its name
 *  ("de" → "German", "AT" or "at" → "Austria"); anything else (a name a person typed, an unknown code) as it is; nothing as "". */
function displayName(value: string | null | undefined, type: "language" | "region"): string {
  const v = (value ?? "").trim();
  if (!/^[a-z]{2}$/i.test(v)) return v;
  try {
    return new Intl.DisplayNames(LOCALE, { type, fallback: "none" }).of(type === "region" ? v.toUpperCase() : v.toLowerCase()) ?? v;
  } catch {
    return v;
  }
}
export const formatLanguage = (value: string | null | undefined): string => displayName(value, "language");
export const formatCountry = (value: string | null | undefined): string => displayName(value, "region");
